import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Loader } from "lucide-react";

import { generateVisibilityReport } from "../api/visibility";
import { useAuth } from "../contexts/AuthContext";
import { useUsage } from "../hooks/useUsage";
import LimitReachedDialog from "./LimitReachedDialog";
import UsageMeter from "./UsageMeter";
import VisibilityErrorDialog from "./VisibilityErrorDialog";
import VisibilityLoaderDialog from "./VisibilityLoaderDialog";

const LLM_RANKING_EXAMPLES = [
  {
    provider: "ChatGPT",
    color: "text-green-400",
    bg: "bg-green-500/10",
    border: "border-green-500/20",
    hotels: ["The Ritz Paris", "Hotel Meurice", "Grand Palace Hotel", "Le Bristol Paris"],
    highlighted: 2,
  },
  {
    provider: "Gemini",
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    hotels: ["Grand Palace Hotel", "Hotel Meurice", "Le Bristol Paris"],
    highlighted: 0,
  },
  {
    provider: "Claude",
    color: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    hotels: ["Hotel Meurice", "Grand Palace Hotel", "The Ritz Paris"],
    highlighted: 1,
  },
];

const LLMVisibilityVisual = () => (
  <div className="rounded-[2.5rem] shadow-2xl w-full ring-1 ring-outline-variant/10 bg-surface-container-low p-6 space-y-3">
    <p className="text-xs text-on-surface-variant font-medium">
      Example: &ldquo;luxury hotels in Paris, France&rdquo;
    </p>
    {LLM_RANKING_EXAMPLES.map((engine) => (
      <div key={engine.provider} className={`rounded-2xl border ${engine.border} ${engine.bg} p-4`}>
        <div className={`text-xs font-bold uppercase tracking-widest mb-3 ${engine.color}`}>
          {engine.provider}
        </div>
        <div className="space-y-1.5">
          {engine.hotels.map((hotel, i) => (
            <div
              key={hotel}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${i === engine.highlighted ? "bg-primary/10 ring-1 ring-primary/30" : "bg-surface-container-lowest/50"}`}
            >
              <span className="text-on-surface-variant/50 font-mono w-4 shrink-0">{i + 1}.</span>
              <span
                className={`truncate flex-1 ${i === engine.highlighted ? "font-bold text-primary" : "text-on-surface"}`}
              >
                {hotel}
              </span>
              {i === engine.highlighted && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-primary/20 text-primary shrink-0">
                  You
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    ))}
    <p className="text-xs text-on-surface-variant/60 text-center">
      Example data — your report will show real results
    </p>
  </div>
);

const PLACEHOLDER_QUERIES = [
  "budget hotels in London",
  "family friendly hotels in Vegas",
  "luxury hotels in Paris",
  "resorts outside New York",
  "spa and retreat outside Bangkok",
  "boutique hotels in Barcelona",
  "beachfront resorts in Bali",
  "romantic getaways in Santorini",
  "business hotels near Tokyo Station",
  "eco-friendly lodges in Costa Rica",
];

const VisibilityEngine = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [limitMessage, setLimitMessage] = useState(null);
  const { byFeature: usage, refresh: refreshUsage } = useUsage();
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [placeholderVisible, setPlaceholderVisible] = useState(true);

  const openAuth = () => navigate("?auth=login");

  // Cycle placeholder text every 3s with fade
  useEffect(() => {
    if (query) return; // stop cycling when user types
    const interval = setInterval(() => {
      setPlaceholderVisible(false);
      setTimeout(() => {
        setPlaceholderIndex((i) => (i + 1) % PLACEHOLDER_QUERIES.length);
        setPlaceholderVisible(true);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, [query]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    if (!user) {
      openAuth();
      return;
    }
    setError(null);
    setLimitMessage(null);
    setIsSubmitting(true);

    try {
      const response = await generateVisibilityReport(query);
      const data = response.data;

      window.dispatchEvent(new CustomEvent("authChange"));
      navigate(`/llm-visibility/report/${data.visibility_report_id}`, {
        state: { visibilityResponse: data },
      });
    } catch (e) {
      if (e.response?.status === 403) {
        refreshUsage();
        setLimitMessage(e.response?.data?.detail || "You've reached your plan limit.");
        setIsSubmitting(false);
        return;
      }
      const detail = e.response?.data?.detail;
      const errorMessage =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((err) => err.msg).join(". ")
            : e.message || "Failed to check visibility.";
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isSubmitting && <VisibilityLoaderDialog query={query} />}

      {error && !isSubmitting && (
        <VisibilityErrorDialog
          onClose={() => setError(null)}
          onRetry={() => {
            setError(null);
            handleSubmit({ preventDefault: () => {} });
          }}
          errorMessage={error}
        />
      )}

      {limitMessage && !isSubmitting && (
        <LimitReachedDialog message={limitMessage} onClose={() => setLimitMessage(null)} />
      )}

      <main className="pt-10 pb-24 px-6 md:px-12 max-w-8xl mx-auto">
        {/* Hero Section */}
        <section className="mb-32" id="visibility-form">
          <div className="mb-6 flex justify-end">
            <button
              className="rounded-lg border border-primary px-4 py-2 font-semibold text-primary"
              onClick={() => navigate("/llm-visibility/aggregates")}
            >
              Aggregate reports
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            {/* Left Column */}
            <div className="lg:col-span-7">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-label text-[10px] font-extrabold tracking-[0.15em] mb-6 uppercase">
                LLM Brand Visibility Tracker
              </span>

              <h1 className="font-headline text-5xl md:text-7xl font-extrabold tracking-tight text-on-surface mb-8 leading-[1.05]">
                Find Out If AI Recommends Your Hotel.
              </h1>

              <p className="font-body text-xl text-on-surface-variant max-w-2xl mb-12 leading-relaxed">
                Enter a travel query that your guests would ask — we send it to ChatGPT, Gemini, and
                Claude in real time, then extract and rank every hotel they mention. See exactly
                where your property stands.
              </p>

              {/* Search Input */}
              <form
                onSubmit={handleSubmit}
                className="glass-effect bg-surface-container-lowest/80 p-2 rounded-xl shadow-xl flex flex-col md:flex-row gap-2 max-w-3xl border border-surface-container-high"
              >
                <div className="flex-grow relative flex items-center bg-surface-container-lowest rounded-lg px-4 border border-outline-variant/20 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-all">
                  <span className="material-symbols-outlined text-outline mr-3">language</span>
                  <div className="relative w-full">
                    <input
                      type="text"
                      required
                      className="w-full py-4 bg-transparent border-none focus:ring-0 text-on-surface font-body text-lg placeholder:text-transparent"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={PLACEHOLDER_QUERIES[placeholderIndex]}
                    />
                    {!query && (
                      <span
                        className={`absolute left-0 top-1/2 -translate-y-1/2 text-lg text-on-surface-variant/50 pointer-events-none transition-opacity duration-300 ${
                          placeholderVisible ? "opacity-100" : "opacity-0"
                        }`}
                      >
                        {PLACEHOLDER_QUERIES[placeholderIndex]}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type={user ? "submit" : "button"}
                  onClick={!user ? openAuth : undefined}
                  className="bg-gradient-to-br from-primary to-primary-container text-on-primary px-10 py-4 rounded-lg font-headline font-bold text-lg hover:opacity-95 transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" /> Checking...
                    </>
                  ) : !user ? (
                    <>
                      Log In to Check Visibility
                      <span className="material-symbols-outlined">login</span>
                    </>
                  ) : (
                    <>
                      Check LLM Visibility{" "}
                      <span className="material-symbols-outlined">analytics</span>
                    </>
                  )}
                </button>
              </form>

              {user && <UsageMeter item={usage?.prompt_tests} className="mt-3 px-2" />}

              <p className="mt-4 text-xs text-on-surface-variant/70 font-label flex items-center gap-2 px-2">
                <span className="material-symbols-outlined text-xs">info</span>
                Results in under 60 seconds. We query three AI providers simultaneously.
              </p>
            </div>

            {/* Right Column — LLM ranking example */}
            <div className="lg:col-span-5 relative hidden lg:block">
              <LLMVisibilityVisual />
            </div>
          </div>
        </section>

        {/* Bento Grid Insights */}
        <section className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-32">
          {/* Semantic Reach Chart */}
          <div className="md:col-span-8 bg-surface-container-low p-8 rounded-xl border border-surface-container-high flex flex-col justify-between">
            <div>
              <h3 className="font-headline text-2xl font-bold mb-2">Visibility Score Breakdown</h3>
              <p className="text-on-surface-variant text-sm mb-8">
                Track how your hotel scores across each AI model — based on mention frequency and
                ranking position.
              </p>
            </div>
            <div className="h-64 flex items-end gap-2 px-4">
              <div className="flex-grow h-full bg-surface-container-lowest rounded-lg p-6 relative overflow-hidden border border-surface-container-high">
                <div className="absolute inset-0 flex items-center justify-center opacity-5">
                  <span className="material-symbols-outlined" style={{ fontSize: "12rem" }}>
                    trending_up
                  </span>
                </div>
                <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 400 100">
                  <defs>
                    <linearGradient id="spark-grad" x1="0%" x2="0%" y1="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: "#2E5BFF", stopOpacity: 0.3 }} />
                      <stop offset="100%" style={{ stopColor: "#2E5BFF", stopOpacity: 0 }} />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0,80 Q50,20 100,50 T200,30 T300,70 T400,20 L400,100 L0,100 Z"
                    fill="url(#spark-grad)"
                  />
                  <path
                    d="M0,80 Q50,20 100,50 T200,30 T300,70 T400,20"
                    fill="none"
                    stroke="#2E5BFF"
                    strokeWidth="3"
                  />
                </svg>
                <div className="absolute top-4 right-4 flex gap-4">
                  <div className="text-right">
                    <div className="text-[10px] font-label font-extrabold uppercase text-on-surface-variant tracking-wider">
                      Avg. Score
                    </div>
                    <div className="text-2xl font-bold text-primary">88.4%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Citations */}
          <div className="md:col-span-4 bg-surface-container-high p-8 rounded-xl border border-surface-container-high">
            <h3 className="font-headline text-2xl font-bold mb-6">Citations</h3>
            <div className="space-y-4">
              <div className="bg-surface-container-lowest p-4 rounded-lg flex items-center gap-4 border border-surface-container-high">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <span
                    className="material-symbols-outlined text-primary"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    format_quote
                  </span>
                </div>
                <div>
                  <div className="font-bold text-sm">Multi-Model Coverage</div>
                  <div className="text-xs text-on-surface-variant">
                    Queries sent to ChatGPT, Gemini & Claude
                  </div>
                </div>
              </div>
              <div className="bg-surface-container-lowest p-4 rounded-lg flex items-center gap-4 border border-surface-container-high">
                <div className="w-10 h-10 rounded-lg bg-surface-container-high flex items-center justify-center">
                  <span className="material-symbols-outlined text-on-surface-variant">link</span>
                </div>
                <div>
                  <div className="font-bold text-sm">Hotel Extraction</div>
                  <div className="text-xs text-on-surface-variant">
                    Every mentioned hotel identified and ranked
                  </div>
                </div>
              </div>
              <div className="bg-surface-container-lowest p-4 rounded-lg flex items-center gap-4 border border-surface-container-high">
                <div className="w-10 h-10 rounded-lg bg-surface-container-high flex items-center justify-center">
                  <span className="material-symbols-outlined text-on-surface-variant">
                    inventory_2
                  </span>
                </div>
                <div>
                  <div className="font-bold text-sm">Competitive Leaderboard</div>
                  <div className="text-xs text-on-surface-variant">
                    See where you stand against every competitor
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Model Categorization */}
          <div className="md:col-span-12 bg-surface p-8 rounded-xl border border-surface-container-high">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
              <div>
                <h3 className="font-headline text-2xl font-bold">What Each AI Model Reveals</h3>
                <p className="text-on-surface-variant text-sm">
                  Different AI models have different training data and biases — see how each one
                  perceives your brand.
                </p>
              </div>
              <div className="flex gap-2">
                <span className="px-3 py-1 bg-surface-container-low border border-surface-container-high rounded-full text-[10px] font-bold tracking-wider">
                  GPT-4o
                </span>
                <span className="px-3 py-1 bg-surface-container-low border border-surface-container-high rounded-full text-[10px] font-bold uppercase tracking-wider">
                  Gemini 1.5
                </span>
                <span className="px-3 py-1 bg-surface-container-low border border-surface-container-high rounded-full text-[10px] font-bold uppercase tracking-wider">
                  Claude 3.5
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-6 bg-surface-container-low rounded-xl border border-surface-container-high">
                <div className="text-[10px] font-label font-extrabold uppercase text-on-surface-variant mb-4 tracking-wider">
                  Mention Rate
                </div>
                <div className="text-3xl font-bold text-on-surface mb-2">3 of 3 LLMs</div>
                <div className="h-1.5 w-full bg-surface-container-high rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: "94%" }} />
                </div>
                <p className="text-xs mt-4 text-on-surface-variant italic">
                  How many AI models include your hotel when asked about your destination.
                </p>
              </div>
              <div className="p-6 bg-surface-container-low rounded-xl border border-surface-container-high">
                <div className="text-[10px] font-label font-extrabold uppercase text-on-surface-variant mb-4 tracking-wider">
                  Average Ranking
                </div>
                <div className="text-3xl font-bold text-on-surface mb-2">Top 3</div>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      className="material-symbols-outlined text-primary text-sm"
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >
                      star
                    </span>
                  ))}
                </div>
                <p className="text-xs mt-4 text-on-surface-variant italic">
                  Your average position across all AI model responses for this query.
                </p>
              </div>
              <div className="p-6 bg-surface-container-low rounded-xl border border-surface-container-high">
                <div className="text-[10px] font-label font-extrabold uppercase text-on-surface-variant mb-4 tracking-wider">
                  Competitor Gap
                </div>
                <div className="text-3xl font-bold text-on-surface mb-2">12 Hotels</div>
                <div className="h-1.5 w-full bg-surface-container-high rounded-full overflow-hidden">
                  <div className="h-full bg-primary/40" style={{ width: "82%" }} />
                </div>
                <p className="text-xs mt-4 text-on-surface-variant italic">
                  Total number of competing properties mentioned across all three AI providers.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-inverse-surface rounded-3xl p-12 md:p-24 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 blur-[120px] rounded-full" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 blur-[120px] rounded-full" />
          <div className="relative z-10">
            <h2 className="font-headline text-4xl md:text-5xl font-extrabold text-inverse-on-surface mb-8 max-w-4xl mx-auto leading-[1.2]">
              Travelers Are Asking AI for Hotel Picks.
              <br />
              Is Yours on the List?
            </h2>
            <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => {
                  document
                    .getElementById("visibility-form")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="bg-primary text-on-primary px-10 py-5 rounded-lg font-headline font-bold text-xl hover:scale-[1.02] transition-transform shadow-xl shadow-primary/20"
              >
                Check Your Visibility
              </button>
            </div>
            <p className="mt-8 text-inverse-on-surface/50 text-xs font-medium tracking-wide">
              Free to try. Sign up to run your first query.
            </p>
          </div>
        </section>
      </main>
    </>
  );
};

export default VisibilityEngine;
